import { LOG } from 'src/app/common/util/log';

import { TableHeaderDefs } from '../types';
import { ColspanBuilder, GroupContext, RowspanBuilder, TableHeaderDefsBuilder, defaultGroupContext } from './layout';
import { allocationHeader, insertBlankHeaderAndCellsHeader } from './special-headers';

export class TableLayoutHandler {
  private readonly hiddenColumns: Set<number> = new Set();
  private readonly groupContext: GroupContext = defaultGroupContext();

  /** column index -> colspan */
  private readonly explicitColspans: Map<number, number> = new Map();

  private readonly rowspanBuilder: RowspanBuilder;
  private readonly colspanBuilder: ColspanBuilder;
  private readonly tableHeaderDefsBuilder: TableHeaderDefsBuilder;

  constructor();
  constructor(
    rowspanBuilder: RowspanBuilder,
    colspanBuilder: ColspanBuilder,
    headerDefsBuilder: TableHeaderDefsBuilder
  );
  constructor(
    rowspanBuilder?: RowspanBuilder,
    colspanBuilder?: ColspanBuilder,
    headerDefsBuilder?: TableHeaderDefsBuilder
  ) {
    this.rowspanBuilder = rowspanBuilder ?? new RowspanBuilder();
    this.colspanBuilder = colspanBuilder ?? new ColspanBuilder();
    this.tableHeaderDefsBuilder = headerDefsBuilder ?? new TableHeaderDefsBuilder();

    this.tableHeaderDefsBuilder.init(this.groupContext, this.hiddenColumns);
    this.colspanBuilder.init(this.explicitColspans);
  }

  public addToColumnGroup(columnIndex: number, groupId: string): void {
    if (this.hiddenColumns.has(columnIndex)) {
      LOG.error(`A hidden column can not be grouped: ${columnIndex}`);
      return;
    }

    this.groupContext.addToGroup(columnIndex, groupId);
  }

  public setHeaderColspan(columnIndex: number, colspanStr: string): void {
    let colspan: number;
    try {
      colspan = Number.parseInt(colspanStr);
    } catch (e) {
      LOG.error(e);
      return;
    }

    if (Number.isNaN(colspan)) {
      LOG.error(`Invalid colspan: '${colspanStr} is NaN`);
      return;
    }

    if (colspan < 0) {
      LOG.error('Colspan must be greater than or equal to 0');
      return;
    }

    if (colspan === 1) {
      LOG.debug('A colspan of 1 has no effect.');
      return;
    }

    this.explicitColspans.set(columnIndex, colspan);
  }

  public hideColumn(columnIndex: number): void {
    if (this.groupContext.groupedColumns.has(columnIndex)) {
      LOG.error(`A grouped column can not be hidden: ${columnIndex}`);
      return;
    }

    this.hiddenColumns.add(columnIndex);
  }

  public buildTableColumnDefinitions(headers: string[]): TableHeaderDefs {
    const headerDefs: TableHeaderDefs = this.tableHeaderDefsBuilder.buildTableHeaderDefs(headers);

    this.colspanBuilder.applyColspans(headerDefs);
    this.rowspanBuilder.buildRowspans(headerDefs);

    this.filterAndMapSpecialHeaders(headerDefs);

    return headerDefs;
  }

  private filterAndMapSpecialHeaders(headerDefs: TableHeaderDefs): void {
    for (let row = 1; row < headerDefs.length; row++) {
      headerDefs[row] = headerDefs[row]
        .filter(header => header !== allocationHeader)
        .map(header => (header === null ? insertBlankHeaderAndCellsHeader : header));
    }
  }
}
